import {
  ResourceIdSchema,
  countryConfigScopeConfigSchema,
  permitConfigConfigSchema
} from "./chunk-ZGJRONN4.mjs";
import {
  fieldSchemas
} from "./chunk-DS3TDJHF.mjs";

// src/country-api/dto/create-country-config-scope.schema.ts
import { z } from "zod";
var createCountryConfigScopeFieldSchemas = {
  /** ID of the resource this scope applies to (CUID) */
  resourceId: ResourceIdSchema,
  /** Type of resource (e.g., 'permit', 'fir', 'airport') */
  resourceType: z.string().min(1),
  /** Optional configuration JSON object for scope-specific settings */
  config: countryConfigScopeConfigSchema.optional()
};
var createCountryConfigScopeSchema = z.object(
  createCountryConfigScopeFieldSchemas
);

// src/country-api/dto/create-permit-config.schema.ts
import { z as z2 } from "zod";
var createPermitConfigFieldSchemas = {
  /** Human-readable name for this permit configuration */
  permitConfigName: z2.string().min(1).max(255),
  /** ISO 2-letter country code this permit applies to */
  countrySlug: fieldSchemas.countrySlug,
  /** Type of flight (e.g., 'overflight', 'landing') */
  flightType: z2.string().min(1),
  /** Type of service (e.g., 'commercial', 'private', 'cargo') */
  serviceType: z2.string(),
  /** Optional description for this permit configuration */
  description: z2.string().optional(),
  /** Extra condition configuration (JSON) */
  extraCondition: z2.any().optional(),
  /** ID of existing permit config to copy config from */
  copyFrom: z2.string().optional()
};
var createPermitConfigSchema = z2.object(
  createPermitConfigFieldSchemas
);

// src/country-api/dto/update-country-config-scope.schema.ts
import { z as z3 } from "zod";
var updateCountryConfigScopeFieldSchemas = {
  /** ID of the resource this scope applies to (CUID format) */
  resourceId: z3.string().optional(),
  /** Type of resource (e.g., 'permit', 'fir', 'airport') */
  resourceType: z3.string().min(1).optional(),
  /** Optional configuration JSON object for scope-specific settings */
  config: countryConfigScopeConfigSchema.optional()
};
var updateCountryConfigScopeSchema = z3.object(
  updateCountryConfigScopeFieldSchemas
);

// src/country-api/dto/update-country-config.schema.ts
import { z as z4 } from "zod";
var updateCountryConfigFieldSchemas = {
  /** ICAO country code (4 letters), e.g., 'EPWA' for Warsaw */
  icaoCode: fieldSchemas.icaoCode.optional(),
  /** IATA country code (3 letters), e.g., 'POL' for Poland */
  iataCode: fieldSchemas.iataCode.optional(),
  /** AIRAC cycle number, e.g., 2401 for January 2024 */
  airacCycleNumber: fieldSchemas.airacCycleNumber.optional()
};
var updateCountryConfigSchema = z4.object(
  updateCountryConfigFieldSchemas
);

// src/country-api/dto/update-permit-config.schema.ts
import { z as z5 } from "zod";
var updatePermitConfigFieldSchemas = {
  /** Human-readable name for this permit configuration */
  permitConfigName: z5.string().min(1).max(255).optional(),
  /** ISO 2-letter country code this permit applies to */
  countrySlug: z5.string().min(2).max(2).toLowerCase().optional(),
  /** Type of flight (e.g., 'overflight', 'landing', 'technical') */
  flightType: z5.string().min(1).optional(),
  /** Type of service (e.g., 'commercial', 'private', 'cargo') */
  serviceType: z5.string().min(1).optional(),
  /** Optional description for this permit configuration */
  description: z5.string().optional(),
  /** Extra condition configuration (JSON) */
  extraCondition: z5.any().optional(),
  /** Permit configuration (JSON) - only updateable after creation */
  config: permitConfigConfigSchema.optional(),
  /** ID of the form template to assign */
  formTemplateId: z5.string().nullable().optional()
};
var updatePermitConfigSchema = z5.object(
  updatePermitConfigFieldSchemas
);

// src/country-api/enums/country-config-status.enum.ts
var CountryConfigStatus = /* @__PURE__ */ ((CountryConfigStatus2) => {
  CountryConfigStatus2["DRAFT"] = "draft";
  CountryConfigStatus2["PUBLISHED"] = "published";
  CountryConfigStatus2["ARCHIVED"] = "archived";
  CountryConfigStatus2["SUPERSEDED"] = "superseded";
  return CountryConfigStatus2;
})(CountryConfigStatus || {});

// src/country-api/enums/event-log-resource-type.enum.ts
var EventLogResourceType = /* @__PURE__ */ ((EventLogResourceType2) => {
  EventLogResourceType2["COUNTRY_CONFIG"] = "COUNTRY_CONFIG";
  EventLogResourceType2["COUNTRY_CONFIG_SCOPE"] = "COUNTRY_CONFIG_SCOPE";
  EventLogResourceType2["DOCUMENT_TYPE"] = "DOCUMENT_TYPE";
  EventLogResourceType2["PERMIT_CONFIG"] = "PERMIT_CONFIG";
  EventLogResourceType2["TENANT"] = "TENANT";
  EventLogResourceType2["SERVICE_PROVIDER"] = "SERVICE_PROVIDER";
  EventLogResourceType2["SERVICE_PROVIDER_COUNTRY"] = "SERVICE_PROVIDER_COUNTRY";
  EventLogResourceType2["SERVICE_PROVIDER_AIRPORT"] = "SERVICE_PROVIDER_AIRPORT";
  EventLogResourceType2["SERVICE_PROVIDER_AIRPORT_SERVICE"] = "SERVICE_PROVIDER_AIRPORT_SERVICE";
  EventLogResourceType2["APPLICATION"] = "Application";
  EventLogResourceType2["APPLICATION_SUBMISSION"] = "ApplicationSubmission";
  EventLogResourceType2["PERMIT"] = "Permit";
  EventLogResourceType2["SUBSCRIPTION"] = "SUBSCRIPTION";
  EventLogResourceType2["COUNTRY_SUBSCRIPTION"] = "COUNTRY_SUBSCRIPTION";
  EventLogResourceType2["CHECKOUT"] = "CHECKOUT";
  return EventLogResourceType2;
})(EventLogResourceType || {});

// src/country-api/enums/event-log-type.enum.ts
var EventLogType = /* @__PURE__ */ ((EventLogType2) => {
  EventLogType2["COUNTRY_CONFIG_CREATED"] = "COUNTRY_CONFIG_CREATED";
  EventLogType2["COUNTRY_CONFIG_UPDATED"] = "COUNTRY_CONFIG_UPDATED";
  EventLogType2["COUNTRY_CONFIG_PUBLISHED"] = "COUNTRY_CONFIG_PUBLISHED";
  EventLogType2["COUNTRY_CONFIG_DISCARDED"] = "COUNTRY_CONFIG_DISCARDED";
  EventLogType2["COUNTRY_CONFIG_ARCHIVED"] = "COUNTRY_CONFIG_ARCHIVED";
  EventLogType2["NEW_DRAFT_STARTED"] = "NEW_DRAFT_STARTED";
  EventLogType2["NEW_AIRAC_CYCLE_STARTED"] = "NEW_AIRAC_CYCLE_STARTED";
  EventLogType2["COUNTRY_CONFIG_ASSIGNED"] = "COUNTRY_CONFIG_ASSIGNED";
  EventLogType2["COUNTRY_CONFIG_UNASSIGNED"] = "COUNTRY_CONFIG_UNASSIGNED";
  EventLogType2["DOCUMENT_TYPE_CREATED"] = "DOCUMENT_TYPE_CREATED";
  EventLogType2["DOCUMENT_TYPE_UPDATED"] = "DOCUMENT_TYPE_UPDATED";
  EventLogType2["DOCUMENT_TYPE_DELETED"] = "DOCUMENT_TYPE_DELETED";
  EventLogType2["PERMIT_CONFIG_CREATED"] = "PERMIT_CONFIG_CREATED";
  EventLogType2["PERMIT_CONFIG_UPDATED"] = "PERMIT_CONFIG_UPDATED";
  EventLogType2["PERMIT_CONFIG_DELETED"] = "PERMIT_CONFIG_DELETED";
  EventLogType2["COUNTRY_CONFIG_SCOPE_CREATED"] = "COUNTRY_CONFIG_SCOPE_CREATED";
  EventLogType2["COUNTRY_CONFIG_SCOPE_UPDATED"] = "COUNTRY_CONFIG_SCOPE_UPDATED";
  EventLogType2["COUNTRY_CONFIG_SCOPE_DELETED"] = "COUNTRY_CONFIG_SCOPE_DELETED";
  EventLogType2["TENANT_CREATED"] = "TENANT_CREATED";
  EventLogType2["TENANT_UPDATED"] = "TENANT_UPDATED";
  EventLogType2["TENANT_DELETED"] = "TENANT_DELETED";
  EventLogType2["SERVICE_PROVIDER_CREATED"] = "SERVICE_PROVIDER_CREATED";
  EventLogType2["SERVICE_PROVIDER_UPDATED"] = "SERVICE_PROVIDER_UPDATED";
  EventLogType2["SERVICE_PROVIDER_DELETED"] = "SERVICE_PROVIDER_DELETED";
  EventLogType2["SERVICE_PROVIDER_STATUS_UPDATED"] = "SERVICE_PROVIDER_STATUS_UPDATED";
  EventLogType2["SERVICE_PROVIDER_COUNTRY_ADDED"] = "SERVICE_PROVIDER_COUNTRY_ADDED";
  EventLogType2["SERVICE_PROVIDER_COUNTRY_UPDATED"] = "SERVICE_PROVIDER_COUNTRY_UPDATED";
  EventLogType2["SERVICE_PROVIDER_AIRPORT_ADDED"] = "SERVICE_PROVIDER_AIRPORT_ADDED";
  EventLogType2["SERVICE_PROVIDER_AIRPORT_REMOVED"] = "SERVICE_PROVIDER_AIRPORT_REMOVED";
  EventLogType2["SERVICE_PROVIDER_SERVICE_ADDED"] = "SERVICE_PROVIDER_SERVICE_ADDED";
  EventLogType2["SERVICE_PROVIDER_SERVICE_REMOVED"] = "SERVICE_PROVIDER_SERVICE_REMOVED";
  EventLogType2["APPLICATION_CREATED"] = "APPLICATION_CREATED";
  EventLogType2["APPLICATION_SUBMISSION_DRAFTED"] = "APPLICATION_SUBMISSION_DRAFTED";
  EventLogType2["APPLICATION_SUBMISSION_DELETED"] = "APPLICATION_SUBMISSION_DELETED";
  EventLogType2["APPLICATION_SUBMISSION_PREPARED"] = "APPLICATION_SUBMISSION_PREPARED";
  EventLogType2["APPLICATION_SUBMISSION_FORM_UPLOADED"] = "APPLICATION_SUBMISSION_FORM_UPLOADED";
  EventLogType2["APPLICATION_SUBMISSION_FORM_DELETED"] = "APPLICATION_SUBMISSION_FORM_DELETED";
  EventLogType2["APPLICATION_SUBMISSION_SENT"] = "APPLICATION_SUBMISSION_SENT";
  EventLogType2["APPLICATION_SUBMISSION_REJECTED"] = "APPLICATION_REJECTED";
  EventLogType2["PERMIT_RECEIVED"] = "PERMIT_RECEIVED";
  EventLogType2["PERMIT_UPDATED"] = "PERMIT_UPDATED";
  EventLogType2["ROUTE_APPLIED"] = "ROUTE_APPLIED";
  EventLogType2["DELAY_APPLIED"] = "DELAY_APPLIED";
  EventLogType2["POSITIONING_APPLIED"] = "POSITIONING_APPLIED";
  EventLogType2["AIRCRAFT_APPLIED"] = "AIRCRAFT_APPLIED";
  EventLogType2["APPLICATION_SUBMISSION_RESOURCES_CONFIRMED"] = "APPLICATION_SUBMISSION_RESOURCES_CONFIRMED";
  EventLogType2["APPLICATION_SUBMISSION_DATA_CONFIRMED"] = "APPLICATION_SUBMISSION_DATA_CONFIRMED";
  EventLogType2["APPLICATION_SUBMISSION_DOCUMENTS_CONFIRMED"] = "APPLICATION_SUBMISSION_DOCUMENTS_CONFIRMED";
  EventLogType2["SUBMISSION_ADDITIONAL_FILE_UPLOADED"] = "SUBMISSION_ADDITIONAL_FILE_UPLOADED";
  EventLogType2["SUBMISSION_ADDITIONAL_FILE_DELETED"] = "SUBMISSION_ADDITIONAL_FILE_DELETED";
  EventLogType2["SUBSCRIPTION_CREATED"] = "SUBSCRIPTION_CREATED";
  EventLogType2["SUBSCRIPTION_UPDATED"] = "SUBSCRIPTION_UPDATED";
  EventLogType2["SUBSCRIPTION_DELETED"] = "SUBSCRIPTION_DELETED";
  EventLogType2["COUNTRY_SUBSCRIPTION_ADDED"] = "COUNTRY_SUBSCRIPTION_ADDED";
  EventLogType2["COUNTRY_SUBSCRIPTION_REMOVED"] = "COUNTRY_SUBSCRIPTION_REMOVED";
  EventLogType2["CHECKOUT_CREATED"] = "CHECKOUT_CREATED";
  EventLogType2["COUNTRY_SUBSCRIPTION_CANCELLED"] = "COUNTRY_SUBSCRIPTION_CANCELLED";
  EventLogType2["COUNTRY_SUBSCRIPTION_REACTIVATED"] = "COUNTRY_SUBSCRIPTION_REACTIVATED";
  EventLogType2["COUNTRY_PRODUCT_SYNCED"] = "COUNTRY_PRODUCT_SYNCED";
  EventLogType2["COUNTRY_PRODUCT_UPDATED"] = "COUNTRY_PRODUCT_UPDATED";
  return EventLogType2;
})(EventLogType || {});

export {
  createCountryConfigScopeFieldSchemas,
  createCountryConfigScopeSchema,
  createPermitConfigFieldSchemas,
  createPermitConfigSchema,
  updateCountryConfigScopeFieldSchemas,
  updateCountryConfigScopeSchema,
  updateCountryConfigFieldSchemas,
  updateCountryConfigSchema,
  updatePermitConfigFieldSchemas,
  updatePermitConfigSchema,
  CountryConfigStatus,
  EventLogResourceType,
  EventLogType
};
